/**
 * Badge / Pill — used for statuses, counts, and inline labels.
 *
 * Variant naming intentionally mirrors API status semantics so call sites
 * read naturally:  <Badge variant="ok">online</Badge>,  <Badge variant="warn">retrying</Badge>.
 *
 * The `dot` prop adds a leading colored dot — common for connection statuses.
 */
import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const badgeStyles = cva(
  "inline-flex items-center gap-1.5 font-semibold rounded-full font-sans",
  {
    variants: {
      variant: {
        ok: "bg-mint/12 text-mint",
        warn: "bg-peach/16 text-peach",
        err: "bg-rose/18 text-rose",
        info: "bg-violet/14 text-violet",
        neutral: "bg-surface-tint text-fg-dim",
      },
      size: {
        sm: "text-[10.5px] px-2 py-[2px]",
        md: "text-xs px-2.5 py-0.5",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "sm",
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeStyles> {
  /** Show a colored leading dot (uses `currentColor`). */
  dot?: boolean;
}

export function Badge({ className, variant, size, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeStyles({ variant, size }), className)} {...props}>
      {dot ? (
        <span
          aria-hidden="true"
          className="inline-block h-1.5 w-1.5 rounded-full bg-current"
        />
      ) : null}
      {children}
    </span>
  );
}
